import { AddressValidatorComponent } from './../../shared/address-validator/address-validator.component';
import { UntypedFormBuilder, ValidationErrors, Validators, UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { ChangeDetectorRef, Component, OnInit, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { sortBy, reject } from 'lodash';
import { OrganizationService } from 'src/app/core/service/organization.service';
import { Observable } from 'rxjs';
import { OrganizationValidators } from 'src/app/core/validators/organization.validators';
import { OrganizationEdit } from '../model/organizationEdit';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { DictionaryService } from 'src/app/core/service/dictionary.service';
import { Code, Dictionary } from 'src/app/core/model/dictionary';
import { HelperService } from 'src/app/core/service/helper.service';
import { SettingService } from 'src/app/core/service/setting.service';
import { GroupResult, groupBy } from '@progress/kendo-data-query';
import { ChannelService } from 'src/app/core/service/channel.service';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';
import { DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { ExtensionService } from 'src/app/core/service/extension.service';
import { ExtensionGridEditHelper } from 'src/app/core/service/extension-grid-edit.helper';
import { ExtensionGridEditConfig } from 'src/app/core/model/extension-grid-edit.model';
import { UserService } from 'src/app/core/service/user.service';

@Component({
  standalone: false,
  selector: 'app-organization-edit',
  templateUrl: './organization-edit.component.html',
  styleUrls: ['./organization-edit.component.scss']
})
export class OrganizationEditComponent implements OnInit
{
  @ViewChild('addressValidator') public addressValidator: AddressValidatorComponent;

  @Input() public isNew = false;

  @Output() public cancelling: EventEmitter<any> = new EventEmitter();
  @Output() public save: EventEmitter<OrganizationEdit> = new EventEmitter();

  public editForm: any;
  public editFormActive = false;
  public organizationTypes: Code[] = [];
  public states: Code[] = [];
  public countries: Code[] = [];
  public phoneNumberMask: string;
  public allChannels = [];
  public groupedChannels: GroupResult[] = [];
  public communityEnabled = false;
  public organizations = [];
  public consentableDataTypes = [];
  public addressChanged = false;
  public addressAPIEnabled = false;
  public saving = false;
  public organizationToSave: any;
  public revenueManager = false;
  public types: any[] = [];
  public assigningAuthorities: any[] = [];
  public viewExtensions: GridViewManager;
  public organizationExtensionsEditable = false;
  public allSettingExtensions = [];
  public unusedSettingExtensions = [];
  public hasExternalSearchSources = false;
  public externalSearchSources = [];
  public patientLabel = {};
  public virtualSearchSource: any = { itemHeight: 30 };

  private organizationEdit: OrganizationEdit;
  private settingExtensionById = new Map<number, any>();

  constructor(
    private fb: UntypedFormBuilder,
    private organizationValidators: OrganizationValidators,
    private messageHandlerService: MessageHandlerService,
    private organizationService: OrganizationService,
    private dictionaryService: DictionaryService,
    private helperService: HelperService,
    private settingService: SettingService,
    private channelService: ChannelService,
    private authenticationService: AuthenticationService,
    public extensionService: ExtensionService,
    private extensionGridEditHelper: ExtensionGridEditHelper,
    private userService: UserService,
    private changeDetectorRef: ChangeDetectorRef)
  {
    // Get Consentable Data Types
    this.dictionaryService.getCachedConsentableDataTypes().subscribe({
      next: (data) =>
      {
        this.consentableDataTypes = data;
      }
    });
    this.dictionaryService.getCachedOrganizationIdentifierTypes().subscribe({
      next: (data) =>
      {
        this.types = data;
      }
    });
    this.dictionaryService.getCachedOrganizationIdentifierAssigningAuthority().subscribe({
      next: (data) =>
      {
        this.assigningAuthorities = data;
      }
    });

    this.viewExtensions = new GridViewManager(99999);
    this.viewExtensions.setData([]);
  }

  public get longitude(): UntypedFormControl
  {
    return this.editForm.get('longitude');
  }

  public get latitude(): UntypedFormControl
  {
    return this.editForm.get('latitude');
  }

  public get referralChannelId(): UntypedFormControl
  {
    return this.editForm.get('referralChannelId');
  }

  public get name(): UntypedFormControl
  {
    return this.editForm.get('name');
  }

  public get organizationType(): UntypedFormControl
  {
    return this.editForm.get('organizationType');
  }

  public get streetLine(): UntypedFormControl
  {
    return this.editForm.get('streetLine');
  }

  public get streetLineAdditional(): UntypedFormControl
  {
    return this.editForm.get('streetLineAdditional');
  }

  public get city(): UntypedFormControl
  {
    return this.editForm.get('city');
  }

  public get county(): UntypedFormControl
  {
    return this.editForm.get('county');
  }

  public get postalCode(): UntypedFormControl
  {
    return this.editForm.get('postalCode');
  }

  public get state(): UntypedFormControl
  {
    return this.editForm.get('state');
  }

  public get country(): UntypedFormControl
  {
    return this.editForm.get('country');
  }

  public get addressId(): UntypedFormControl
  {
    return this.editForm.get('addressId');
  }

  public get addressVersion(): UntypedFormControl
  {
    return this.editForm.get('addressVersion');
  }

  public get lastName(): UntypedFormControl
  {
    return this.editForm.get('lastName');
  }

  public get firstName(): UntypedFormControl
  {
    return this.editForm.get('firstName');
  }

  public get homePhoneNumber(): UntypedFormControl
  {
    return this.editForm.get('homePhoneNumber');
  }

  public get homePhoneNumberExtension(): UntypedFormControl
  {
    return this.editForm.get('homePhoneNumberExtension');
  }

  public get cellPhoneNumber(): UntypedFormControl
  {
    return this.editForm.get('cellPhoneNumber');
  }

  public get faxNumber(): UntypedFormControl
  {
    return this.editForm.get('faxNumber');
  }

  public get title(): UntypedFormControl
  {
    return this.editForm.get('title');
  }

  public get email(): UntypedFormControl
  {
    return this.editForm.get('email');
  }

  public get consentExpirationMonths(): UntypedFormControl
  {
    return this.editForm.get('consentExpirationMonths');
  }

  public get allowTribal(): UntypedFormControl
  {
    return this.editForm.get('allowTribal');
  }

  public get consentCollects(): UntypedFormControl
  {
    return this.editForm.get('consentCollects');
  }

  public get consentViews(): UntypedFormControl
  {
    return this.editForm.get('consentViews');
  }

  public get searchSources(): UntypedFormControl
  {
    return this.editForm.get('searchSources');
  }

  public get extensions(): UntypedFormControl
  {
    return this.editForm.get('extensions');
  }

  @Input() public set model(organization: OrganizationEdit)
  {
    this.dictionaryService.getCachedPatientSearchSources().subscribe({
      next: (data) =>
      {
        this.externalSearchSources = data.filter(x => x.code !== 'CHANNELS');
        this.hasExternalSearchSources = this.externalSearchSources.length > 0;
        this.initModel(organization);
      }
    });
  }

  public getOrganizationExtensionDataType(dataItem: any): string
  {
    return this.extensionGridEditHelper.getDataType(this.getExtensionGridConfig(), dataItem);
  }

  public getOrganizationExtensionValueMaxLength(dataItem: any): number
  {
    return this.extensionGridEditHelper.getValueMaxLength(this.getExtensionGridConfig(), dataItem);
  }

  public getOrganizationExtensionValueInputKey(dataItem: any): string
  {
    return this.extensionGridEditHelper.getValueInputKey(this.getExtensionGridConfig(), dataItem);
  }

  public getOrganizationExtensionNumberFormat(dataItem: any): string
  {
    return this.extensionGridEditHelper.getNumberFormat(this.getExtensionGridConfig(), dataItem);
  }

  public getOrganizationExtensionNumberDecimals(dataItem: any): number
  {
    return this.extensionGridEditHelper.getNumberDecimals(this.getExtensionGridConfig(), dataItem);
  }

  public isOrganizationExtensionValueEditorVisible(dataItem: any): boolean
  {
    return this.extensionGridEditHelper.isValueEditorVisible(dataItem);
  }

  public ngOnInit(): void
  {
    this.revenueManager = this.authenticationService.currentUser.role === 'ROLE_REVENUE_MANAGER_OPERATOR';
    this.dictionaryService.getCachedOrganizationTypes().subscribe({
      next: (data) =>
      {
        this.organizationTypes = sortBy(data, ['displayName']);
      }
    });

    // get list of states/provinces from Dictionary table
    this.helperService.getStatesCountriesFromMultipleSources().subscribe({
      next: (responseList) =>
      {
        this.states = responseList[0];
        this.countries = responseList[1];
      }
    });
  }

  public onSave(e: Event): void
  {
    e.preventDefault();

    const extensions = this.extensionService.prepareExtensionsForSave(this.viewExtensions.gridData.data);
    if (extensions === null)
    {
      this.messageHandlerService.displayWarning('EXTENSION-ROW-EDIT-IN-PROGRESS');
      return;
    }

    this.editForm.value.extensions = extensions;
    // Must remove temporary guid from extensions
    this.editForm.value.extensions.forEach((e) => { if (!Number.isFinite(e.id)) { e.id = null; } });

    const objectToPersist = this.editForm.value;

    // Set Channel
    if (this.communityEnabled && this.referralChannelId.value > 0)
    {
      objectToPersist.referralChannelId = this.referralChannelId.value;
    }
    else
    {
      objectToPersist.referralChannelId = null;
    }

    // Clean up departments
    this.setDepartments(objectToPersist);
    this.setIdentifiers(objectToPersist);
    this.setAddress(objectToPersist);

    this.performOrganizationSave(objectToPersist);
  }

  /**
   * Perform the Organization Save.
   */
  public saveOrganization(): void
  {
    // Emit save event
    this.save.emit(this.organizationToSave);
    this.addressChanged = false;
    this.saving = false;
    this.editFormActive = false;
  }

  /**
   * Set Consent permission
   *
   * @param dataItem Checked or unchecked dictionary item
   * @param consentList Consent list to change
   * @param event Checkbox event
   */
  public setConsent(dataItem: any, consentList: Dictionary[], event: any): void
  {
    this.editForm.markAsDirty();
    if (event.target.checked)
    {
      consentList.push(dataItem);
    }
    else
    {
      const index = consentList.findIndex(x => x.id === dataItem.id);
      if (index >= 0)
      {
        consentList.splice(index, 1);
      }
    }
  }

  /**
   * Check if Consent option is checked
   *
   * @param dictionaryId Dictionary Id to check
   * @param consentList Consent list to check
   * @returns Returns true if the item is checked.
   */
  public isConsentChecked(dictionaryId: number, consentList: Dictionary[]): boolean
  {
    return consentList.findIndex(x => x.id === dictionaryId) >= 0;
  }

  public onCancel(e: Event): void
  {
    e.preventDefault();
    this.closeEditForm();
  }

  /**
   * Address change event from Address Validator component.
   *
   * @param event Address data being selected to apply.
   */
  public changeAddress(event: any): void
  {
    // Override the address based on Address API response.
    if (event.confirmation === 'recommended')
    {
      // Force dirty to change state
      this.editForm.markAsDirty();

      // Set form properties
      this.streetLine.setValue(event.line1);
      this.streetLineAdditional.setValue(event.line2);
      this.state.setValue(event.postalAddress && event.postalAddress.administrativeArea ?
        this.states.find(x => x.code === event.postalAddress.administrativeArea) : null);
      this.city.setValue(event.postalAddress && event.postalAddress.locality ? event.postalAddress.locality : null);
      this.postalCode.setValue(event.postalAddress && event.postalAddress.postalCode ? event.postalAddress.postalCode : null);
      this.latitude.setValue(event.geocode && event.geocode.location ? event.geocode.location.latitude : null);
      this.longitude.setValue(event.geocode && event.geocode.location ? event.geocode.location.longitude : null);

      if (event.postalAddress && event.postalAddress.regionCode)
      {
        this.country.setValue(this.countries.find(x => x.alternateCode === event.postalAddress.regionCode));
      }
      this.addressChanged = false;    // Mark address unchanged to avoid opening modal again unless it is manually changed again.
    }
    else
    {
      this.latitude.setValue(null);
      this.longitude.setValue(null);
    }

    // If autoSave, then set patient object and save.
    if (event.autoSave)
    {
      this.setAddress(this.organizationToSave);
      this.saveOrganization();
    }
  }

  /**
   * Manual Address Validation. No autoSave will be performed.
   */
  public validateAddress(): void
  {
    this.addressValidator.execute(false, this.streetLine.value, this.streetLineAdditional.value, this.city.value,
      this.state.value ? this.state.value.code : null, this.country.value ? this.country.value.code : null, this.postalCode.value);
  }

  public addDepartment(id: number = null, name: string = null, active = true, version = 0): void
  {
    this.editForm.controls.departments.push(this.initDepartment(id, name, active, version));
    this.editForm.markAsDirty();
  }

  public removeDepartment(i: number, id: number): void
  {
    const control = this.editForm.controls.departments;
    // If this id was already persisted, we must remove
    if (id > 0)
    {
      // Validate if already in use
      this.organizationService.existsDepartment(id).subscribe({
        next: (inUseResponse) =>
        {
          if (inUseResponse)
          {
            this.messageHandlerService.displayWarning('DEPARTMENT-IN-USE-CANNOT-DELETE');
          }
          else
          {
            control.removeAt(i);
            this.editForm.touched = true;
            this.editForm.markAsDirty();
          }
        }
      });
    }
    else
    {
      control.removeAt(i);
      this.editForm.touched = true;
      this.editForm.markAsDirty();
    }
  }

  public addIdentifier(id: number = null, identifierValue: string = null, type: any = null, assigningAuthority: any = null, version = 0): void
  {
    this.editForm.controls.identifiers.push(this.initIdentifier(id, identifierValue, type, assigningAuthority, version));
    this.editForm.markAsDirty();
  }

  public removeIdentifier(i: number, id: number): void
  {
    const control = this.editForm.controls.identifiers;
    // If this id was already persisted, we must remove
    if (id > 0)
    {
      const rejectedId: any = {};
      rejectedId.id = id;
      this.organizationEdit.identifiers = reject(this.organizationEdit.identifiers, rejectedId);
    }
    control.removeAt(i);
    this.editForm.touched = true;
    this.editForm.markAsDirty();
  }

  public itemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return !itemArgs.dataItem.active;
  }

  public handleOrganizationChange(value: any): void
  {
    if (value === undefined)
    {
      this.editForm.controls.parentOrganization.setValue(null);
    }
  }

  /**
   * Check if item from dropdown is disabled or enabled.
   *
   * @returns boolean
   */
  public channelItemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return itemArgs.dataItem.status !== 'Online';
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public organizationExtensionsDataStateChange(state: DataStateChangeEvent): void
  {
    this.viewExtensions.setState(state);
  }

  public organizationExtensionAddNew(): void
  {
    this.extensionGridEditHelper.addNew(this.getExtensionGridConfig());
  }

  public organizationExtensionStartEdit(dataItem: any): void
  {
    this.extensionGridEditHelper.startEditRow(this.getExtensionGridConfig(), dataItem);
  }

  public organizationExtensionCancelRow(dataItem: any): void
  {
    this.extensionGridEditHelper.cancelRow(this.getExtensionGridConfig(), dataItem);
  }

  public deleteOrganizationExtension(extension: any): void
  {
    this.extensionGridEditHelper.deleteExtension(this.getExtensionGridConfig(), extension);
  }

  public getUnusedExtensions(reset: boolean): void
  {
    this.extensionGridEditHelper.loadSettingExtensions(this.getExtensionGridConfig(), reset);
  }

  public organizationExtensionSaveRow(dataItem: any): void
  {
    this.extensionGridEditHelper.saveRow(this.getExtensionGridConfig(), dataItem);
  }

  public formatExtensionValue(dataItem: any): string
  {
    return this.extensionGridEditHelper.formatExtensionValue(this.getExtensionGridConfig(), dataItem);
  }

  public isOrganizationExtensionTextAreaValue(dataItem: any): boolean
  {
    return this.extensionGridEditHelper.isTextAreaValue(this.getExtensionGridConfig(), dataItem);
  }

  public onOrganizationExtensionValueInput(event: Event, dataItem: any): void
  {
    this.extensionGridEditHelper.onValueInput(this.getExtensionGridConfig(), event, dataItem);
  }

  public onOrganizationExtensionValuePaste(event: Event, dataItem: any): void
  {
    this.extensionGridEditHelper.onValuePaste(this.getExtensionGridConfig(), event, dataItem);
  }

  public onOrganizationExtensionSettingExtensionChange(settingExtension: any, dataItem: any): void
  {
    this.extensionGridEditHelper.onSettingExtensionChange(this.getExtensionGridConfig(), settingExtension, dataItem);
  }

  public onOrganizationExtensionValueKeydown(event: KeyboardEvent, dataItem: any): void
  {
    this.extensionGridEditHelper.onValueKeydown(this.getExtensionGridConfig(), event, dataItem);
  }

  private getExtensionGridConfig(): ExtensionGridEditConfig
  {
    return {
      category: 'ORGANIZATION',
      valueRowIdAttr: 'data-org-extension-value-row-id',
      extensions: this.extensions,
      viewExtensions: this.viewExtensions,
      allSettingExtensions: this.allSettingExtensions,
      unusedSettingExtensions: this.unusedSettingExtensions,
      settingExtensionById: this.settingExtensionById,
      changeDetectorRef: this.changeDetectorRef,
      markDirty: () => this.editForm.markAsDirty(),
      afterAddNew: () => this.getUnusedExtensions(false),
    };
  }

  private setDepartments(objectToPersist: any): void
  {
    objectToPersist.departments.forEach(d =>
    {
      delete d.rowId;
    });
  }

  private setIdentifiers(objectToPersist: any): void
  {
    objectToPersist.identifiers.forEach(d =>
    {
      delete d.rowId;
    });
  }

  private setAddress(objectToPersist: any): void
  {
    objectToPersist.address = {};
    objectToPersist.address.id = this.addressId.value;
    objectToPersist.address.version = this.addressVersion.value;
    objectToPersist.address.streetLine = this.streetLine.value;
    objectToPersist.address.streetLineAdditional = this.streetLineAdditional.value;
    objectToPersist.address.postalCode = this.postalCode.value;
    objectToPersist.address.city = this.city.value;
    objectToPersist.address.county = this.county.value;
    objectToPersist.address.state = this.state.value;
    objectToPersist.address.country = this.country.value;
    objectToPersist.address.longitude = this.longitude && this.longitude.value !== '' ? this.longitude.value : null;
    objectToPersist.address.latitude = this.latitude && this.latitude.value !== '' ? this.latitude.value : null;

    delete objectToPersist.streetLine;
    delete objectToPersist.streetLineAdditional;
    delete objectToPersist.city;
    delete objectToPersist.county;
    delete objectToPersist.postalCode;
    delete objectToPersist.state;
    delete objectToPersist.country;
    delete objectToPersist.addressId;
    delete objectToPersist.addressVersion;
    delete objectToPersist.latitude;
    delete objectToPersist.longitude;
  }

  private closeEditForm(): void
  {
    this.editFormActive = false;
    this.cancelling.emit();
  }

  private initModel(organization: OrganizationEdit)
  {
    if (organization === undefined)
    {
      return;
    }

    this.organizationEdit = organization;

    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.phoneNumberMask = sessionSetting.phoneMask;
        this.communityEnabled = sessionSetting.communityEnabled;
        this.patientLabel = { patientLabel: sessionSetting.patientLabel };
        this.addressAPIEnabled = sessionSetting.addressAPIEnabled;
        this.organizationExtensionsEditable = (sessionSetting.addUpdateExtensionsRoles !== '') ?
          this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.addUpdateExtensionsRoles) : false;

        if (this.communityEnabled && !this.isNew)
        {
          this.channelService.getCacheMinimalChannels(`?organizationId=${organization.id}&referralOnly=true`).subscribe({
            next: (channelData) =>
            {
              this.allChannels = channelData;
              this.allChannels.forEach(d =>
              {
                d.programName = d.program.name;
              });
              this.setGroupedChannels();
            }
          });
        }
      }
    });

    // Set the current organization name used for name async validators
    const previousOrganizationName = this.isNew ? '' : organization.name;

    // Set form
    this.editForm = this.fb.group({
      id: [null],
      name: [
        { value: '', disabled: false },
        {
          validators: [Validators.required],
          asyncValidators: (): Observable<ValidationErrors | null> =>
            this.organizationValidators.validateOrganizationNameUnique(this.editForm.get('name'), previousOrganizationName)
        }
      ],
      comments: { value: '', disabled: false },
      parentOrganization: { value: null, disabled: false },
      active: { value: true, disabled: false },
      departments: this.fb.array([]),
      identifiers: this.fb.array([]),
      consentCollects: [{
        value: organization.consentCollects, disabled: false
      }],
      consentViews: [{
        value: organization.consentViews, disabled: false
      }],
      searchSources: [{
        value: organization.searchSources, disabled: false
      }],
      organizationType: [{ value: organization.organizationType, disabled: false }, Validators.required],
      referralChannelId: {
        value: organization.referralChannelId ? organization.referralChannelId : null,
        disabled: this.isNew
      },
      // Address
      streetLine: [{ value: null, disabled: false }],
      streetLineAdditional: [{ value: null, disabled: false }],
      city: [{ value: null, disabled: false }],
      county: [{ value: null, disabled: false }],
      postalCode: [{ value: null, disabled: false }],
      state: [{ value: null, disabled: false }],
      country: [{ value: null, disabled: false }],
      addressId: [null],
      addressVersion: [null],
      longitude: null,
      latitude: null,
      // Contact
      firstName: [null],
      lastName: [null],
      email: [null],
      homePhoneNumber: [null],
      homePhoneNumberExtension: [null],
      cellPhoneNumber: [null],
      faxNumber: [null],
      title: [null],
      consentExpirationMonths: [null],
      allowTribal: [null],
      submittingOrganization: { value: false, disabled: false },
      extensions: [{
        value: organization.extensions !== null ?
          organization.extensions : [], disabled: false
      }],
    });

    if (organization && organization.departments)
    {
      organization.departments = sortBy(organization.departments, ['name']);
      organization.departments.forEach(data =>
      {
        this.addDepartment(data.id, data.name, data.active, data.version);
      });
    }

    if (this.communityEnabled)
    {
      const channelId = this.referralChannelId && this.referralChannelId.value ? this.referralChannelId.value : null;
      if (channelId !== null)
      {
        const channelFound = this.allChannels.filter(x => x.id === channelId);
        if (channelFound.length === 0)
        {
          this.channelService.getMinimalChannelById(channelId).subscribe({
            next: (lookupChannel) =>
            {
              if (lookupChannel.length === 1)
              {
                lookupChannel.forEach(d =>
                {
                  d.programName = d.program.name;
                });

                // Add Channel to allChannels array to show but will not be able to select (will be disabled)
                this.allChannels.push(lookupChannel[0]);
                this.setGroupedChannels();
              }
              else
              {
                // Skip the load since we can't retrieve the channelId.
                this.messageHandlerService.displayWarning('CHANNEL-NOT-FOUND');
              }
            }
          });
        }
      }
    }

    this.editForm.reset(organization);

    if (organization && organization.identifiers)
    {
      organization.identifiers = sortBy(organization.identifiers, ['name']);
      organization.identifiers.forEach(data =>
      {
        this.addIdentifier(data.id, data.value, data.typeDictionary, data.assigningAuthorityDictionary, data.version);
      });
    }

    if (organization && organization.extensions)
    {
      const gridConfig = this.getExtensionGridConfig();
      organization.extensions.forEach((extension) =>
      {
        this.extensionGridEditHelper.cacheSettingExtensionDefinition(gridConfig, extension?.settingExtension);
      });
      const extensions = organization.extensions ? organization.extensions : [];
      this.viewExtensions.setData(extensions);
      this.viewExtensions.setSort([{ field: 'settingExtension.name', dir: 'asc' }]);
      this.extensionGridEditHelper.resolveDictionaryDisplayNames(gridConfig, extensions);
    }

    if (!this.isNew)
    {
      this.getUnusedExtensions(false);
    }

    if (organization.address !== null && organization.address !== undefined)
    {
      this.streetLine.setValue(organization.address.streetLine);
      this.streetLineAdditional.setValue(organization.address.streetLineAdditional);
      this.city.setValue(organization.address.city);
      this.county.setValue(organization.address.county);
      this.postalCode.setValue(organization.address.postalCode);
      this.state.setValue(organization.address.state);
      this.country.setValue(organization.address.country);
      this.addressId.setValue(organization.address.id);
      this.addressVersion.setValue(organization.address.version);
    }

    // Monitor address changes to not show address validator all the time.
    this.addressChanged = false;
    this.editForm.get('streetLine').valueChanges.subscribe({ next: () => { this.addressChanged = true; } });
    this.editForm.get('streetLineAdditional').valueChanges.subscribe({ next: () => { this.addressChanged = true; } });
    this.editForm.get('city').valueChanges.subscribe({ next: () => { this.addressChanged = true; } });
    this.editForm.get('state').valueChanges.subscribe({ next: () => { this.addressChanged = true; } });
    this.editForm.get('country').valueChanges.subscribe({ next: () => { this.addressChanged = true; } });
    this.editForm.get('postalCode').valueChanges.subscribe({ next: () => { this.addressChanged = true; } });

    this.organizationEdit = organization;
    this.editFormActive = organization !== undefined;
    this.getOrganizations();
  }

  private initDepartment(id = 0, name: string, active: boolean, version: number): UntypedFormGroup
  {
    return this.fb.group({
      rowId: null,
      id: [id],
      version: [version],
      name: [name, Validators.required],
      active: [active]
    });
  }

  private initIdentifier(id = 0, identifierValue: string, type: any, assigningAuthority: any, version: number): UntypedFormGroup
  {
    const group: any = {};
    group.id = [id];
    group.value = [{ value: identifierValue, disabled: false }, Validators.required];
    group.typeDictionary = [{ value: type, disabled: false }, Validators.required];
    group.assigningAuthorityDictionary = [{ value: assigningAuthority, disabled: false }];
    group.version = version;
    return this.fb.group(group);
  }

  private getOrganizations()
  {
    this.organizationService.getCachedOrganizations().subscribe({
      next: (data) =>
      {
        this.organizations = data;
        if (this.organizationEdit != null)
        {
          this.organizations = this.organizations.filter(
            org => org.id !== this.organizationEdit.id
          );
        }
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  private setGroupedChannels(): void
  {
    // Build the grouped Channels
    const groupedChannels = groupBy(this.allChannels, [{ field: 'programName' }]);
    this.groupedChannels.forEach(d =>
    {
      const items = sortBy(d.items, ['name']);
      d.items = items;
    });
    this.groupedChannels = sortBy(groupedChannels, ['value']);
  }

  private performOrganizationSave(organization: any): void
  {
    this.saving = true;
    this.organizationToSave = organization;
    if (this.addressChanged && this.streetLine.value)
    {
      this.addressValidator.execute(true, this.streetLine.value, this.streetLineAdditional.value, this.city.value,
        this.state.value ? this.state.value.code : null, this.country.value ? this.country.value.code : null, this.postalCode.value);
    }
    else
    {
      this.saveOrganization();
    }
  }
}
